import {
  useGetAllSessionsQuery,
  useAddSessionMutation,
  useGetSessionByIdQuery,
  useUpdateSessionMutation,
  useDeleteSessionMutation,
} from "../api/sessionApi";
import { SessionResponseType } from "../types/sessionResponse";
import { SessionPayloadType } from "../types/sessionRequest";
import { getApiErrorMessage } from "../../../../core/utils/errorHandler";
import { PaginationProps } from "../../../../core/data/interface";

export const useSession = (params?: PaginationProps & { skip?: boolean }) => {
  const { skip, ...queryParams } = params || {};
  const { data, isLoading, isError, error } = useGetAllSessionsQuery(
    queryParams,
    { skip }
  );

  const [
    addSessionMutation,
    { isLoading: isAdding, isError: isAddError, error: addError },
  ] = useAddSessionMutation();

  const [
    updateSessionMutation,
    { isLoading: isUpdating, isError: isUpdateError, error: updateError },
  ] = useUpdateSessionMutation();

  const [
    deleteSessionMutation,
    { isLoading: isDeleting, isError: isDeleteError, error: deleteError },
  ] = useDeleteSessionMutation();

  const errorMessage = isError ? getApiErrorMessage(error) : null;
  const addErrorMessage = isAddError ? getApiErrorMessage(addError) : null;
  const updateErrorMessage = isUpdateError
    ? getApiErrorMessage(updateError)
    : null;
  const deleteErrorMessage = isDeleteError
    ? getApiErrorMessage(deleteError)
    : null;
  const sessions: SessionResponseType[] = data?.data?.items || [];

  const paginationData = {
    totalPages: data?.data?.totalPages,
    pageSize: data?.data?.pageSize,
    totalItems: data?.data?.totalItems,
    currentPage: data?.data?.currentPage,
  };

  const addNewSession = async (payload: SessionPayloadType) => {
    try {
      const response = await addSessionMutation(payload).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: addErrorMessage || "Failed to add session",
        errorData: error
      };
    }
  };

  const useGetSessionById = (id: string) => {
    const { data } = useGetSessionByIdQuery({ id }, { skip: !id });
    return data?.data.academicSession;
  };

  const updateSession = async (id: string, payload: SessionPayloadType) => {
    try {
      const response = await updateSessionMutation({ id, payload }).unwrap();
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: getApiErrorMessage(error) || "Failed to update session",
        errorData: error
      };
    }
  };

  const deleteSession = async (id: string) => {
    try {
      const response = await deleteSessionMutation({ id }).unwrap();
      return response;
    } catch (error) {
      return error;
    }
  };

  const getSessionState = () => ({
    sessions,
    isLoading,
    error: errorMessage,
  });

  return {
    getSessionState,
    paginationData,
    useGetSessionById,
    addNewSession,
    updateSession,
    deleteSession,
    isAdding,
    isUpdating,
    isDeleting,
    addError: addErrorMessage,
    updateError: updateErrorMessage,
    deleteError: deleteErrorMessage,
  };
};
